import { ModalService } from '../../../base/shared/modal/modal.service';
import { ExportInterfaceActivationEmailTemplateDTO, ExportInterfaceConfigurationTransferType, IUpdateExportInterfaceActivationEmailTemplateDTO, UpdateExportInterfaceActivationEmailTemplateDTO } from 'src/app/base/models/models.swagger.generated';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Component, OnInit, ViewChild } from '@angular/core';
import { merge, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ActivationEmailTemplatesOverviewComponent } from '../shared/activation-email-templates-overview/activation-email-templates-overview.component';
import { ActivationEmailTemplatesOverviewService } from '../shared/activation-email-templates-overview/activation-email-templates-overview.service';
import { TranslateModule } from '@ngx-translate/core';
import { ActivationEmailTemplateFormComponent } from './activation-email-template-form/activation-email-template-form.component';

import { TitleBarComponent } from '../../../base/shared/title-bar/title-bar.component';
import { ActivationEmailTemplateFormType } from './activation-email-template-form/activation-email-template-form.type';
import { NavigationEnd, Router } from '@angular/router';
import { activationEmailTemplatesExpConfRoute } from 'src/app/core/routing/route-names';
import { UrlHelper } from 'src/app/base/shared/url-helper';
import * as pages from '../../../core/routing/page-names';
import { ToastService } from 'src/app/base/services/toast.service';

@Component({
    selector: 'app-activation-email-templates',
    templateUrl: './activation-email-templates.component.html',
    styleUrls: ['./activation-email-templates.component.scss'],
    imports: [TitleBarComponent, ActivationEmailTemplatesOverviewComponent, ActivationEmailTemplateFormComponent, TranslateModule]
})
export class ActivationEmailTemplatesComponent implements OnInit{

  @ViewChild(ActivationEmailTemplatesOverviewComponent) templateOverviewComponent: ActivationEmailTemplatesOverviewComponent;

  public activationEmailTemplate: ExportInterfaceActivationEmailTemplateDTO;
  public activationEmailTemplateForm: FormGroup<ActivationEmailTemplateFormType>;

  public selectedTemplateId : number;

  constructor(
    private templateService: ActivationEmailTemplatesOverviewService,
    private fb: FormBuilder,
    private modalService: ModalService,
    private router: Router,
    private toastService: ToastService
  ) { }

  ngOnInit() {
    this.initForm();

    this.router.events.pipe(filter(event => event instanceof NavigationEnd && event.url.includes(activationEmailTemplatesExpConfRoute))).subscribe(() => {
      this.urlChanged();
    });

    // call the first time urlChanged on our own
    this.urlChanged();
  }

  public urlChanged(): void {
    var id = UrlHelper.getIdForSegment(pages.activationEmailTemplatesPageName, this.router)
    if (id != null){
      this.selectedTemplateId = id;
    } else {
      this.selectedTemplateId = null;
    }
  }

  public addTemplate() {
    this.templateService.addRecord().subscribe((data: ExportInterfaceActivationEmailTemplateDTO) => {
      this.templateOverviewComponent.recordAdded(data.id);
    });
  }

  public closeTemplate() {
    this.templateOverviewComponent.resetRowSelected();
    this.activationEmailTemplate = null;
    this.activationEmailTemplateForm.reset();
    this.activationEmailTemplateForm.markAsPristine();
    this.router.navigate([activationEmailTemplatesExpConfRoute]);
  }

  public deleteTemplate() {
    const dialog = this.modalService.showOkCancelDialog('i18n.domain.export-configuration.activation-email-templates.delete-dialog.title', 'i18n.domain.export-configuration.activation-email-templates.delete-dialog.question');
    dialog.onOkSubject.subscribe(() => {
      this.templateService.deleteRecord(this.activationEmailTemplate.id).subscribe(() => {
        this.templateOverviewComponent.refresh();
      });
      this.closeTemplate();
    });
  }

  public saveTemplate() {
    this.activationEmailTemplateForm.markAllAsTouched();
    if (this.activationEmailTemplateForm.invalid) {
      this.toastService.showWarning('i18n.domain.export-configuration.configurations.messages.form-invalid.title',
        'i18n.domain.export-configuration.configurations.messages.form-invalid.message', null);
      return;
    }
    const templateValues = this.activationEmailTemplateForm.getRawValue() as IUpdateExportInterfaceActivationEmailTemplateDTO;
    templateValues.id = this.activationEmailTemplate.id;
    this.templateService.updateRecord(new UpdateExportInterfaceActivationEmailTemplateDTO(templateValues)).subscribe({next: () => {
      this.closeTemplate();
      this.templateOverviewComponent.refresh();
    }, error: (error) => {
      // we just catch since warnings are handled in service
    }});
  }

  public templateSelected(selectedActivationEmailTemplate: ExportInterfaceActivationEmailTemplateDTO) {
    this.activationEmailTemplate = selectedActivationEmailTemplate;
    this.loadDataToForm(selectedActivationEmailTemplate);
    if (selectedActivationEmailTemplate?.id != null) {
      this.router.navigate([activationEmailTemplatesExpConfRoute + "/" + selectedActivationEmailTemplate.id]);
    } else {
      this.router.navigate([activationEmailTemplatesExpConfRoute]);
    }
  }

  private initForm() {
    this.activationEmailTemplateForm = this.fb.group<ActivationEmailTemplateFormType>({
      id: this.fb.control<number>(null),
      templateName: this.fb.control<string>(null, [Validators.required]),
      transferType: this.fb.control<ExportInterfaceConfigurationTransferType>(null, [Validators.required]),
      senderName: this.fb.control<string>(null, [Validators.required]),
      subject: this.fb.control<string>(null, [Validators.required]),
      textHTML: this.fb.control<string>(null, [Validators.required]),
      emailAccountId: this.fb.control<number>(null, [Validators.required]),
    });
  }

  private loadDataToForm(data: ExportInterfaceActivationEmailTemplateDTO) {
    this.activationEmailTemplateForm = this.fb.group<ActivationEmailTemplateFormType>({
      id: this.fb.control<number>(data.id),
      templateName: this.fb.control<string>(data.templateName, [Validators.required]),
      transferType: this.fb.control<ExportInterfaceConfigurationTransferType>(data.transferType, [Validators.required]),
      senderName: this.fb.control<string>(data.senderName, [Validators.required]),
      subject: this.fb.control<string>(data.subject, [Validators.required]),
      textHTML: this.fb.control<string>(data.textHTML, [Validators.required]),
      emailAccountId: this.fb.control<number>(data.emailAccountId, [Validators.required]),
    });
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.activationEmailTemplateForm.dirty) {
      const dialog = this.modalService.showOkCancelDialog('i18n.domain.export-configuration.activation-email-templates.discard-dialog.title', 'i18n.domain.export-configuration.activation-email-templates.discard-dialog.question');
      return merge(
        dialog.onOkSubject.pipe(map(() => true)),
        dialog.onCancelSubject.pipe(map(() => false)),
      );
    }
    return true;
  }
}
